import { useState } from 'react';

import {
  gradeBadgeClasses,
  gradeLabel,
  type ConceptGrade,
  type ReviewOutcome,
  type ReviewOutcomes,
  type SubmissionState,
} from '@/lib/practice/grades';
import type { ConceptResolver } from '@/lib/practice/concepts';
import { cn } from '@/lib/utils';

/** Concepts listed before "Show all": a 34-concept lesson listed 34 rows of
 *  "Good" and pushed the screen's actions out of sight. */
export const SHOWN_AT_FIRST = 6;

/**
 * "What this quiz/deck did to your revision schedule" summary, shown on both
 * the quiz and flashcard completed screens. Each concept gets one grade
 * (aggregated from its questions/cards). A concept counts once per day toward
 * FSRS, so a concept already reviewed today (via the other tab or an earlier
 * attempt) is shown as "already counted today" rather than claimed as added.
 *
 * The weakest come first (Again, Hard, Good, Easy), and only the first
 * `SHOWN_AT_FIRST` until the student asks for all of them.
 */
export function ConceptReviewSummary({
  grades,
  resolveConcept,
  outcomes,
  submission,
}: {
  grades: ConceptGrade[];
  resolveConcept: ConceptResolver;
  /** Per-concept applied/deduped result, keyed by item concept_id. */
  outcomes: ReviewOutcomes;
  submission: SubmissionState;
}) {
  const [showAll, setShowAll] = useState(false);
  if (grades.length === 0) return null;
  // Weakest first; the lesson's order within a grade.
  const sorted = grades
    .map((g, i) => ({ g, i }))
    .sort((a, b) => a.g.grade - b.g.grade || a.i - b.i)
    .map(({ g }) => g);
  const shown = showAll ? sorted : sorted.slice(0, SHOWN_AT_FIRST);

  return (
    // No fill: it sits on the lesson panel, which already carries the surface.
    // A solid near-white box read as a separate card stuck on top of it.
    <div className='mb-5 w-full rounded-xl border border-border/60 p-4 text-left'>
      <div className='mb-1 flex items-center gap-2'>
        <h3 className='text-sm font-semibold text-foreground'>Your concepts</h3>
        {submission === 'submitting' && (
          <span className='ml-auto text-xs text-muted-foreground'>saving…</span>
        )}
      </div>
      {/* Plain words for the student; the FSRS mechanics (once a day per
          concept, earlier result kept) live in the badges' tooltips. */}
      <p className='mb-3 text-xs leading-relaxed text-muted-foreground'>
        Weaker ones come back for review sooner.
      </p>
      {/* Expanded, the list scrolls in its own box (about the height of the
          rows shown before), so the screen around it - heading, buttons -
          stays where it is instead of the whole panel scrolling. */}
      {/* A scrollable box must take keyboard focus (axe:
          scrollable-region-focusable), so it is a named region. */}
      <div
        className={cn(
          showAll && 'max-h-48 overflow-y-auto overscroll-contain pr-1'
        )}
        role={showAll ? 'region' : undefined}
        tabIndex={showAll ? 0 : undefined}
        aria-label={showAll ? 'All your concepts' : undefined}
      >
        <ul className='space-y-1.5'>
          {shown.map(g => (
            <ConceptRow
              key={g.conceptId}
              grade={g}
              resolveConcept={resolveConcept}
              outcome={outcomes[g.conceptId]}
            />
          ))}
        </ul>
      </div>
      {sorted.length > SHOWN_AT_FIRST && (
        <button
          type='button'
          onClick={() => setShowAll(v => !v)}
          aria-expanded={showAll}
          className='mt-3 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          {showAll ? 'Show fewer' : `Show all ${sorted.length} concepts`}
        </button>
      )}
    </div>
  );
}

function ConceptRow({
  grade: g,
  resolveConcept,
  outcome,
}: {
  grade: ConceptGrade;
  resolveConcept: ConceptResolver;
  outcome: ReviewOutcome | undefined;
}) {
  const resolved = resolveConcept(g.conceptId);
  const canonicalId = resolved?.canonicalId;
  // Status: not-in-map yet → pending; submitted but server said dupe →
  // already counted today; otherwise it landed on the schedule.
  const status: 'pending' | 'counted' | 'added' =
    outcome === 'pending' || (!canonicalId && !outcome)
      ? 'pending'
      : outcome === 'deduped'
        ? 'counted'
        : 'added';

  return (
    <li className='flex items-center justify-between gap-3 text-sm'>
      <span className='text-foreground'>
        {resolved?.name || 'Unknown concept'}
      </span>
      <span className='flex items-center gap-1.5'>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${gradeBadgeClasses(
            g.grade
          )}`}
        >
          {gradeLabel(g.grade)}
        </span>
        {status === 'counted' && (
          <span
            className='text-xs text-muted-foreground'
            title='This concept was already reviewed today (here or on the other tab); your earlier result stands.'
          >
            counted today
          </span>
        )}
        {status === 'pending' && (
          <span
            className='text-xs text-muted-foreground'
            title="This concept is still being added to your knowledge map; your result is kept and scheduled once it's there."
          >
            added shortly
          </span>
        )}
      </span>
    </li>
  );
}
